import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { ApiService } from "../services/api";
import { rememberPendingVideoCredit } from "../utils/videoCreditSettlement";
import "./create_video.scss";

const PROVIDERS = {
  "no-limit": {
    provider: "a2eWan",
    title: "No Limit Videos (Image to Video)",
    shortTitle: "No Limit",
    description: "Upload a starting image, then guide the motion with your prompt.",
    icon: "fi-rr-bolt",
    className: "a2e-choice",
    resolutions: ["480p", "720p", "1080p"],
    durations: [5, 10].map((seconds) => ({
      label: `${seconds} Seconds`,
      value: `${seconds}s`,
    })),
    ratios: ["16:9", "9:16", "1:1", "4:3", "3:4"],
    defaultRatio: "9:16",
    defaultRes: "720p",
    supportsImage: true,
    requiresImage: true,
    creditsPerSecond: { "480p": 2, "720p": 3, "1080p": 4 },
  },
  nsfw: {
    provider: "wan",
    title: "No Limit Videos",
    shortTitle: "NSFW",
    description: "Flexible generations for mature creative prompts. Generate anything!",
    icon: "fi-rr-flame",
    className: "nsfw-choice",
    resolutions: ["480p", "580p", "720p"],
    durations: [5, 6, 8, 10, 12, 14, 16, 20].map((seconds) => ({
      label: `${seconds} Seconds`,
      value: `${seconds}s`,
    })),
    ratios: ["16:9", "9:16"],
    defaultRatio: "16:9",
    defaultRes: "480p",
    supportsImage: true,
    creditsPerSecond: { "480p": 1, "580p": 2, "720p": 2 },
  },
  cinematic: {
    provider: "grok",
    title: "High Cinematic videos with Sound",
    shortTitle: "Cinematic",
    description: "Premium cinematic generations with synchronized sound.",
    icon: "fi-rr-clapperboard-play",
    className: "cinematic-choice",
    resolutions: ["480p", "720p"],
    durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((seconds) => ({
      label: `${seconds} Seconds`,
      value: `${seconds}s`,
    })),
    ratios: ["1:1", "16:9", "9:16"],
    defaultRatio: "1:1",
    defaultRes: "480p",
    supportsImage: true,
    creditsPerSecond: { "480p": 1, "720p": 2 },
  },
  general: {
    provider: "luma",
    title: "General Purpose videos",
    shortTitle: "General",
    description: "Clean, versatile generations for everyday scenes.",
    icon: "fi-rr-sparkles",
    className: "general-choice",
    resolutions: ["180p", "360p", "540p", "720p", "1080p"],
    durations: ["5s", "9s"].map((value) => ({
      label: `${value.replace("s", "")} Seconds`,
      value,
    })),
    ratios: ["1:1", "16:9", "9:16", "4:3", "3:4"],
    defaultRatio: "16:9",
    defaultRes: "540p",
    supportsImage: true,
    creditsPerSecond: { "180p": 1, "360p": 1, "540p": 1, "720p": 2, "1080p": 3 },
  },

};

const providerCards = [
  { mode: "no-limit", ...PROVIDERS["no-limit"] },
  // { mode: "nsfw", ...PROVIDERS.nsfw },
  { mode: "general", ...PROVIDERS.general },
  { mode: "cinematic", ...PROVIDERS.cinematic },
];

const getUserIdentifier = () => {
  return localStorage.getItem("phoneNumber") || localStorage.getItem("userEmail") || "";
};

const isJpgOrPng = (file) => {
  const validTypes = ["image/jpeg", "image/png"];
  const validExtension = /\.(jpe?g|png)$/i.test(file.name || "");
  return validTypes.includes(file.type) || validExtension;
};

const CreateVideo = () => {
  const navigate = useNavigate();
  const { mode } = useParams();
  const fileInputRef = useRef(null);
  const config = PROVIDERS[mode];

  const [availableCredits, setAvailableCredits] = useState(0);
  const [userId, setUserId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedRatio, setSelectedRatio] = useState(config?.defaultRatio || "16:9");
  const [selectedRes, setSelectedRes] = useState(config?.defaultRes || "480p");
  const [selectedDuration, setSelectedDuration] = useState("5s");
  const [notice, setNotice] = useState(null);
  const [acceptedResponsibility, setAcceptedResponsibility] = useState(false);

  useEffect(() => {
    if (!config) return;
    setSelectedRatio(config.defaultRatio);
    setSelectedRes(config.defaultRes);
    setSelectedDuration(config.durations[0].value);
    setAcceptedResponsibility(false);
    removeImage();
  }, [mode, config]);

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const activeId = getUserIdentifier();
      setUserId(activeId);

      if (!activeId) return;

      const response = await ApiService.getUserInfo(activeId);
      const data = response.data || response;
      if (data) {
        setAvailableCredits(Number(data.availableCredits) || 0);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
    }
  };

  const calculateCredits = () => {
    if (!config) return 0;
    const seconds = parseInt(selectedDuration, 10);
    const ratePerSecond = config.creditsPerSecond[selectedRes] || 1;
    return seconds * ratePerSecond;
  };

  const requiredCredits = calculateCredits();
  const hasLowBalance = availableCredits < requiredCredits;

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (config?.provider === "a2eWan" && !isJpgOrPng(file)) {
      event.target.value = "";
      setNotice({
        title: "JPG or PNG Required",
        message: "No Limit Videos accepts starting images in JPG or PNG format only.",
        action: "upload",
      });
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    setNotice(null);
  };

  function removeImage() {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const handleGenerate = async () => {
    if (!config) return;
    if (config.requiresImage && !selectedImage) {
      setNotice({
        title: "Reference Image Required",
        message: "This video style starts from an uploaded JPG or PNG image. Add an image, then generate again.",
        action: "upload",
      });
      return;
    }
    if (!acceptedResponsibility) {
      setNotice({
        title: "User Responsibility Required",
        message: "Please accept the User Responsibility terms before creating a video.",
        action: "responsibility",
      });
      return;
    }
    if (hasLowBalance) {
      navigate("/pricing");
      return;
    }
    if (!prompt.trim() || isGenerating) return;

    setIsGenerating(true);

    try {
      const generationProvider = config.provider;
      const providerResolution =
        generationProvider === "wan" && selectedRes === "1080p"
          ? "720p"
          : selectedRes;
      const providerRatio =
        generationProvider === "wan" && !["16:9", "9:16"].includes(selectedRatio)
          ? "16:9"
          : selectedRatio;

      const formData = new FormData();
      formData.append("prompt", prompt);
      formData.append("userId", userId);
      formData.append("aspect_ratio", providerRatio);
      formData.append("resolution", providerResolution);

      if (config.provider === "grok") {
        formData.append("quality", "High");
        formData.append("duration", selectedDuration.replace("s", ""));
      }

      if (config.provider === "luma") {
        formData.append("quality", selectedRes === "1080p" ? "High" : "Standard");
        formData.append("duration", selectedDuration);
        formData.append("loop", "false");
      }

      if (generationProvider === "wan") {
        formData.append("duration", selectedDuration);
        formData.append("enable_safety_checker", "false");
        formData.append("video_quality", "medium");
      }

      if (generationProvider === "a2eWan") {
        formData.append("duration", selectedDuration);
        formData.append("audio", "false");
      }

      if (selectedImage && config.supportsImage) {
        formData.append("image", selectedImage);
      }

      const data = await ApiService.startProviderVideoGeneration(
        generationProvider,
        formData
      );

      if (data && data.jobId) {
        rememberPendingVideoCredit({
          jobId: data.jobId,
          userId,
          amount: requiredCredits,
        });
        navigate(`/video-result/${generationProvider}/${data.jobId}`);
      } else {
        throw new Error(data?.error || data?.details || "Video generation failed");
      }
    } catch (error) {
      console.error("Process failed", error);
      setNotice({
        title: "Generation Could Not Start",
        message: error.message || "Something went wrong while starting your video.",
        action: "dismiss",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  if (!config) {
    return (
      <div className="zullu-generate-container picker-mode">
        <div className="video-choice-header">
          <h1>Create AI Video</h1>
          <p>Choose the generation style that fits your idea.</p>
        </div>

        <div className="video-provider-grid">
          {providerCards.map((card) => (
            <button
              key={card.mode}
              className={`video-provider-card ${card.className}`}
              onClick={() => navigate(`/create-video/${card.mode}`)}
            >
              <span className="provider-icon">
                <i className={`fi ${card.icon}`}></i>
              </span>
              <span className="provider-copy">
                <b>{card.title}</b>
                <small>{card.description}</small>
              </span>
              <span className="provider-action">
                Select
                <i className="fi fi-rr-arrow-small-right"></i>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const imageAccept = config?.provider === "a2eWan" ? "image/png,image/jpeg" : "image/*";

  return (
    <div className="zullu-generate-container">
      <div className="center-content provider-center">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <button className="back-provider-btn" onClick={() => navigate("/create-video")}>
            <i className="fi fi-rr-arrow-small-left"></i>
            All video types
          </button>
          <span className="provider-kicker">{config.shortTitle}</span>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
          {config.requiresImage && (
            <div className="image-required-badge">
              <i className="fi fi-rr-picture"></i>
              JPG or PNG image required
            </div>
          )}
          <AnimatePresence>
            {hasLowBalance && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                className="center-warning-toast"
              >
                Insufficient credits. This video requires {requiredCredits} credits.
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      <div className="fixed-bottom-bar">
        <div className="command-card">
          {config.requiresImage && !imagePreview && (
            <button
              type="button"
              className="required-image-callout"
              onClick={() => fileInputRef.current.click()}
            >
              <span className="callout-icon">
                <i className="fi fi-rr-picture"></i>
              </span>
              <span className="callout-copy">
                <b>Add a starting image</b>
                <small>This mode animates your uploaded JPG or PNG image into a video.</small>
              </span>
              <span className="callout-action">Upload</span>
            </button>
          )}

          <AnimatePresence>
            {imagePreview && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className={`image-preview-wrapper ${config.requiresImage ? "featured" : ""}`}
              >
                <div className="preview-container">
                  <img src={imagePreview} alt="Upload preview" />
                  <button className="remove-img-btn" onClick={removeImage}>
                    <i className="fi fi-rr-cross-small"></i>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="input-area">
            {config.supportsImage && (
              <div className="plus-icon" onClick={() => fileInputRef.current.click()}>
                <i className={selectedImage ? "fi fi-sr-picture" : "fi fi-rr-picture"}></i>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept={imageAccept}
                  hidden
                />
              </div>
            )}
            <textarea
              placeholder="Describe the scene, motion, camera style, and mood..."
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
            />
          </div>

          <label className="responsibility-check">
            <input
              type="checkbox"
              checked={acceptedResponsibility}
              onChange={(event) => setAcceptedResponsibility(event.target.checked)}
            />
            <span>
              I accept the{" "}
              <button type="button" onClick={() => navigate("/user-responsibility")}>
                User Responsibility
              </button>
            </span>
          </label>

          <div className="toolbar-area">
            <div className="dropdowns-group">
              <div className="custom-select">
                <i className="fi fi-rr-expand"></i>
                <select
                  value={selectedRatio}
                  onChange={(event) => setSelectedRatio(event.target.value)}
                >
                  {config.ratios.map((ratio) => (
                    <option key={ratio} value={ratio}>
                      {ratio}
                    </option>
                  ))}
                </select>
              </div>
              <div className="custom-select">
                <i className="fi fi-rr-clock"></i>
                <select
                  value={selectedDuration}
                  onChange={(event) => setSelectedDuration(event.target.value)}
                >
                  {config.durations.map((duration) => (
                    <option key={duration.value} value={duration.value}>
                      {duration.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="custom-select">
                <i className="fi fi-rr-settings-sliders"></i>
                <select
                  value={selectedRes}
                  onChange={(event) => setSelectedRes(event.target.value)}
                >
                  {config.resolutions.map((resolution) => (
                    <option key={resolution} value={resolution}>
                      {resolution}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="actions-group">
              <span className={`credit-label ${hasLowBalance ? "red" : ""}`}>
                {requiredCredits} Credits required
              </span>
              <button
                className={`main-btn ${hasLowBalance ? "top-up" : ""} ${
                  !acceptedResponsibility ? "blocked" : ""
                }`}
                onClick={handleGenerate}
                disabled={isGenerating || !acceptedResponsibility}
              >
                {isGenerating ? (
                  <div className="btn-spinner"></div>
                ) : hasLowBalance ? (
                  <div className="lock-content">
                    <i className="fi fi-rr-lock"></i> Create
                  </div>
                ) : (
                  <i className="fi fi-rr-arrow-small-up"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {notice && (
          <motion.div
            className="generation-notice-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setNotice(null)}
          >
            <motion.div
              className="generation-notice"
              initial={{ opacity: 0, y: 18, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.96 }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                className="notice-close"
                onClick={() => setNotice(null)}
                aria-label="Close"
              >
                <i className="fi fi-rr-cross-small"></i>
              </button>
              <div className="notice-icon">
                <i className="fi fi-rr-picture"></i>
              </div>
              <h3>{notice.title}</h3>
              <p>{notice.message}</p>
              <button
                className="notice-primary"
                onClick={() => {
                  setNotice(null);
                  if (notice.action === "upload" && config.supportsImage) {
                    fileInputRef.current.click();
                  }
                  if (notice.action === "responsibility") {
                    navigate("/user-responsibility");
                  }
                }}
              >
                {notice.action === "upload"
                  ? "Upload Image"
                  : notice.action === "responsibility"
                  ? "Read Terms"
                  : "Got It"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateVideo;
